import React from "react";
import { API } from "@/lib/api";
import { useAuthProviders } from "@/hooks/useAuthProviders";

/**
 * Phase 3.5B — Social sign-in buttons.
 *
 * Renders provider buttons ONLY for providers returned by
 * `GET /api/auth/providers`. If the endpoint returns an empty list
 * (feature flags OFF), the entire block is hidden — no greyed-out state,
 * no divider, nothing.
 */
export default function SocialLoginButtons({
  mode = "login",       // "login" | "register" | "link"
  returnTo = "",        // safe path like "/dashboard" or "/claim"
  showDivider = true,
}) {
  const { providers, loading } = useAuthProviders();
  if (loading || !providers || providers.length === 0) return null;

  const startUrl = (p) => {
    const params = new URLSearchParams();
    params.set("mode", mode === "register" ? "login" : mode);
    if (returnTo) params.set("return_to", returnTo);
    return `${API}/auth/oauth/${p}/start?${params.toString()}`;
  };

  const cta = mode === "register" ? "Sign up" : "Continue";

  return (
    <div data-testid="social-login-block" className="space-y-3">
      <div className="grid grid-cols-1 gap-3">
        {providers.includes("google") && (
          <a
            href={startUrl("google")}
            data-testid="social-google-btn"
            className="w-full inline-flex items-center justify-center gap-3 rounded-xl border-2 border-[var(--ink)] bg-white px-4 py-3 text-sm font-bold text-[var(--ink)] shadow-[3px_3px_0_var(--ink)] hover:shadow-[5px_5px_0_var(--ink)] hover:-translate-x-0.5 hover:-translate-y-0.5 transition"
          >
            <GoogleGlyph /> {cta} with Google
          </a>
        )}
        {providers.includes("apple") && (
          <a
            href={startUrl("apple")}
            data-testid="social-apple-btn"
            className="w-full inline-flex items-center justify-center gap-3 rounded-xl border-2 border-[var(--ink)] bg-[var(--ink)] px-4 py-3 text-sm font-bold text-white shadow-[3px_3px_0_var(--panini-yellow)] hover:shadow-[5px_5px_0_var(--panini-yellow)] hover:-translate-x-0.5 hover:-translate-y-0.5 transition"
          >
            <AppleGlyph /> {cta} with Apple
          </a>
        )}
      </div>
      {showDivider && (
        <div className="flex items-center gap-3 py-2" data-testid="social-divider">
          <div className="h-px flex-1 bg-[var(--line)]" />
          <span className="text-[11px] font-bold tracking-widest text-[var(--smoke)] uppercase">
            or with email
          </span>
          <div className="h-px flex-1 bg-[var(--line)]" />
        </div>
      )}
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

function AppleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M16.365 1.43c0 1.14-.417 2.243-1.093 3.05-.716.865-1.897 1.532-3.05 1.442-.135-1.108.418-2.28 1.084-3.048.755-.867 1.995-1.516 3.06-1.444zM20.66 17.65c-.55 1.267-.815 1.833-1.526 2.955-.992 1.565-2.39 3.516-4.122 3.532-1.542.014-1.94-1.003-4.033-.99-2.093.012-2.532 1.007-4.076.994-1.732-.017-3.058-1.778-4.05-3.343-2.778-4.376-3.07-9.51-1.357-12.24 1.216-1.94 3.135-3.08 4.94-3.08 1.836 0 2.99.998 4.507.998 1.47 0 2.365-.998 4.487-.998 1.605 0 3.303.874 4.52 2.386-3.972 2.175-3.324 7.849.71 8.786z"/>
    </svg>
  );
}
